import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  CART_MAX_ITEMS_PER_SELLER,
  isSellerCartFull,
  findCartItem,
  quantityLimitsFor,
} from '../../util/custom/cart';
import { addListingToCart, selectCart, selectCartItemCount } from '../../ducks/cart.duck';
import { PrimaryButton } from '../../components';

import css from './AddToCartButton.module.css';

/**
 * CL-CART-01 — "Add to cart" on the listing page (requirements 1 and 2).
 *
 * After adding, the buyer stays exactly where they are and a short confirmation appears.
 * Navigating away from the listing on every add is the fastest way to stop somebody
 * building a basket.
 *
 * The toast carries a "View cart" link. It is a plain href rather than a NamedLink because
 * react-toastify renders its content outside the Router's tree, where NamedLink has no
 * route context to resolve against.
 */
export const AddToCartButtonComponent = props => {
  const {
    className,
    rootClassName,
    listingId,
    stockType,
    currentStock,
    isOwnListing,
    isInCart,
    isCartFull,
    cartItemCount,
    title,
    onAddToCart,
  } = props;

  const intl = useIntl();

  const { allowsMultiple, hasStock } = quantityLimitsFor(stockType, currentStock);

  // The seller sees the button exactly as a buyer would, so the listing page previews
  // truthfully. It cannot add anything: like the template's Buy now on an own listing, a
  // click only scrolls up to the "This is your own listing" bar. AC-5's other half — the
  // server rejecting a direct API call — is server-side and not this component's job.
  if (isOwnListing) {
    return (
      <div className={classNames(rootClassName || css.root, className)}>
        <PrimaryButton type="button" disabled={!hasStock} onClick={() => window.scrollTo(0, 0)}>
          {hasStock ? (
            <FormattedMessage id="AddToCartButton.addToCart" />
          ) : (
            <FormattedMessage id="AddToCartButton.outOfStock" />
          )}
        </PrimaryButton>
      </div>
    );
  }

  // AC-3: for a one-of-a-kind listing already held, the button says so rather than
  // silently doing nothing. A listing that allows multiples says something different
  // again — it can be added to, and the label has to make that obvious rather than
  // looking like the same button that did nothing a moment ago.
  const alreadyHeld = isInCart && !allowsMultiple;
  const canAddMore = isInCart && allowsMultiple;
  // The cap counts distinct rows, so a listing already in the cart is not blocked by it.
  const blockedByCap = isCartFull && !isInCart;
  const disabled = !hasStock || alreadyHeld || blockedByCap;

  // Toast FIRST, then let the write finish. The add reaches Redux and localStorage
  // synchronously; only the profile mirror is a network call, and that one is explicitly
  // allowed to fail without blocking the buyer. Waiting for it meant a buyer on a slow
  // connection clicked and saw nothing happen.
  //
  // No spinner for the same reason: there is nothing to wait for, and a spinner would
  // claim otherwise.
  //
  // toastId keyed to the listing so a double click replaces the toast instead of stacking
  // two identical ones.
  const handleClick = () => {
    toast.success(
      // Text on the left, action on the right — the same shape as the cart's Undo toast, so
      // "the thing to press" sits in one place across both.
      <span className={css.toastBody}>
        <span>
          {intl.formatMessage(
            { id: 'AddToCartButton.addedToast' },
            { title, count: cartItemCount + 1 }
          )}
        </span>
        <a className={css.toastLink} href="/cart">
          {intl.formatMessage({ id: 'AddToCartButton.viewCart' })}
        </a>
      </span>,
      { toastId: `cart-${listingId}` }
    );
    return Promise.resolve(onAddToCart());
  };

  const label = !hasStock ? (
    <FormattedMessage id="AddToCartButton.outOfStock" />
  ) : alreadyHeld ? (
    <FormattedMessage id="AddToCartButton.alreadyInCart" />
  ) : canAddMore ? (
    <FormattedMessage id="AddToCartButton.addAnother" />
  ) : (
    <FormattedMessage id="AddToCartButton.addToCart" />
  );

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      <PrimaryButton type="button" disabled={disabled} onClick={handleClick}>
        {label}
      </PrimaryButton>

      {blockedByCap ? (
        <p className={css.note}>
          <FormattedMessage
            id="AddToCartButton.cartFull"
            values={{ max: CART_MAX_ITEMS_PER_SELLER }}
          />
        </p>
      ) : null}
    </div>
  );
};

// `isOwnListing` is passed in, not derived here: OrderPanel already computes it, and
// deriving it a second time from currentUser would be a second answer to the same
// question.
const mapStateToProps = (state, ownProps) => {
  const { listing } = ownProps;
  const authorId = listing?.author?.id?.uuid;
  const cart = selectCart(state);

  return {
    listingId: listing?.id?.uuid,
    isInCart: !!findCartItem(cart, authorId, listing?.id?.uuid),
    isCartFull: isSellerCartFull(cart, authorId),
    cartItemCount: selectCartItemCount(state),
    title: listing?.attributes?.title,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onAddToCart: () => {
    // The cap is computed here, from the listing type, and stored on the cart row. The
    // cart module cannot work it out itself — it never sees a listing type.
    const { maxQuantity } = quantityLimitsFor(ownProps.stockType, ownProps.currentStock);
    return dispatch(addListingToCart({ listing: ownProps.listing, maxQuantity }));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddToCartButtonComponent);
